/**
 * Company-wide registers for credit notes and payments.
 *
 * Both records already existed underneath the invoice they belong to. These
 * two screens put every one of them in a single searchable list, because the
 * questions people actually ask — "how much has this vendor credited us this
 * year", "did we ever pay that bill" — cannot be answered one invoice at a
 * time.
 *
 * Clicking a row opens the whole record, including anything filed against it.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Ban, CreditCard, FileText, HandCoins, Paperclip, Plus, Receipt, ReceiptText, Wallet } from 'lucide-react'
import {
  Alert, Card, Confirm, EmptyState, Field, Input, Loading, Modal, SearchInput, Select, Stat,
  Textarea, Th, sortRows, useSort, useToast,
} from '@/components/ui'
import {
  AttachmentCollector, AttachmentList, queueAttachments, type PendingAttachment,
} from '@/components/docs/shared'
import { DocumentDate, todayIso } from '@/components/docs/DocumentDate'
import { useAuth } from '@/lib/auth'
import { can } from '@/lib/domain'
import { usePeople, useQuery, useVendors } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { ddmmyyyy, dateTime, rupees } from '@/lib/format'

const PAYMENT_MODES = ['NEFT', 'RTGS', 'IMPS', 'UPI', 'Cheque', 'Demand Draft', 'Cash', 'Adjustment']

/* ------------------------------------------------------------------ credits */

export function CreditNotes() {
  const { profile } = useAuth()
  // Names, not email addresses — management 01-09-2026. See src/lib/people.ts.
  const people = usePeople()
  const [vendor, setVendor] = useState('')
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')
  const [open, setOpen] = useState<any | null>(null)
  const [newOpen, setNewOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const { data: vendors } = useVendors()
  const { sort, toggle } = useSort('credit_date')

  // Booking a credit note is accounts-payable work — it is what closes the
  // loop when a vendor bills for material that was rejected. Accounts and
  // Procurement both do it; the Head of Stores sees every one and records none.
  const mayCredit = can(profile!.role, 'createCreditNote')
  // Deleting one is the opposite act: it puts money BACK on what the vendor is
  // owed. Whoever can do that and also pay him holds both halves of a fraud,
  // so it stays with Procurement.
  const mayDeleteCredit = can(profile!.role, 'deleteCreditNote')

  const { data: rows, loading, refresh } = useQuery<any>('v_credit_note_register', () => {
    let s = supabase.from('v_credit_note_register').select('*')
    if (vendor) s = s.eq('vendor_id', vendor)
    if (status) s = s.eq('credit_status', status)
    return s.order('credit_date', { ascending: false }).limit(1000)
  }, [vendor, status], { cacheable: false })

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase()
    if (!t) return rows
    return rows.filter((r: any) => [r.id, r.credit_no, r.vendor_name, r.invoice_no, r.reason]
      .some((v) => String(v ?? '').toLowerCase().includes(t)))
  }, [rows, q])

  const sorted = useMemo(() => sortRows(filtered, sort, {
    credit_date: 'date', created_at: 'date', taxable_amount: 'number',
    gst_amount: 'number', total_amount: 'number', invoice_total: 'number',
    attachment_count: 'number',
  }), [filtered, sort])

  const totals = useMemo(() => filtered.reduce((a: any, r: any) => ({
    all: a.all + Number(r.total_amount || 0),
    active: a.active + (r.credit_status === 'ACTIVE' ? Number(r.total_amount || 0) : 0),
  }), { all: 0, active: 0 }), [filtered])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-bold text-slate-800">Credit notes</h1>
        <span className="pill-grey">{filtered.length}</span>
        {mayCredit && (
          <button className="btn-primary btn-sm ml-auto" onClick={() => setNewOpen(true)}>
            <Plus className="h-4 w-4" /> New credit note
          </button>
        )}
      </div>

      <Alert tone="info" title="What a credit note is doing here">
        A credit note is the vendor admitting he billed too much — an over-charge, a rate
        correction, or material sent back. It reduces what he is owed. It stays
        <strong> active</strong> until the invoice it offsets is settled, at which point it has
        done its job and shows as <strong>closed</strong>.
      </Alert>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Credit notes" value={filtered.length} icon={<ReceiptText className="h-5 w-5" />} />
        <Stat label="Total credited" value={rupees(totals.all)} tone="good" />
        <Stat label="Still active" value={rupees(totals.active)} tone="warn" />
        <Stat label="Vendors involved"
              value={new Set(filtered.map((r: any) => r.vendor_id)).size} />
      </div>

      <Card title="All credit notes" subtitle="Click any row to see the full note and what was filed with it">
        <div className="mb-3 grid gap-3 sm:grid-cols-3">
          <Field label="Vendor">
            <Select value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="All vendors"
                    options={vendors.map((v) => ({ value: v.id, label: v.company_name }))} />
          </Field>
          <Field label="Status">
            <Select value={status} onChange={(e) => setStatus(e.target.value)} placeholder="Active and closed"
                    options={[{ value: 'ACTIVE', label: 'Active' }, { value: 'CLOSED', label: 'Closed' }]} />
          </Field>
          <Field label="Search">
            <SearchInput value={q} onChange={setQ} placeholder="Credit number, vendor, invoice, reason…" />
          </Field>
        </div>

        {loading && <Loading />}
        {!loading && sorted.length === 0 && (
          <EmptyState icon={<ReceiptText className="h-6 w-6" />} title="No credit notes"
                      message="None have been recorded against any invoice yet." />
        )}
        {!loading && sorted.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Reference</Th>
                  <Th id="credit_no" sort={sort} onSort={toggle}>Vendor's number</Th>
                  <Th id="credit_date" sort={sort} onSort={toggle}>Date</Th>
                  <Th id="vendor_name" sort={sort} onSort={toggle}>Vendor</Th>
                  <Th id="invoice_no" sort={sort} onSort={toggle}>Against invoice</Th>
                  <Th id="taxable_amount" sort={sort} onSort={toggle} className="num" align="right">Taxable</Th>
                  <Th id="gst_amount" sort={sort} onSort={toggle} className="num" align="right">GST</Th>
                  <Th id="total_amount" sort={sort} onSort={toggle} className="num" align="right">Credit</Th>
                  <Th id="credit_status" sort={sort} onSort={toggle}>Status</Th>
                  <Th id="attachment_count" sort={sort} onSort={toggle} className="num" align="right">Files</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r: any) => (
                  <tr key={r.id} className="cursor-pointer hover:bg-oval-50" onClick={() => setOpen(r)}>
                    <td className="font-mono text-xs">{r.id}</td>
                    <td className="text-sm font-medium">{r.credit_no}</td>
                    <td className="text-xs">{ddmmyyyy(r.credit_date)}</td>
                    <td className="text-sm">{r.vendor_name}</td>
                    <td className="font-mono text-xs">{r.invoice_no}</td>
                    <td className="num text-xs">{rupees(r.taxable_amount)}</td>
                    <td className="num text-xs">{rupees(r.gst_amount)}</td>
                    <td className="num text-sm font-bold text-emerald-700">{rupees(r.total_amount)}</td>
                    <td>
                      <span className={r.credit_status === 'ACTIVE' ? 'pill-amber' : 'pill-grey'}>
                        {r.credit_status === 'ACTIVE' ? 'Active' : 'Closed'}
                      </span>
                    </td>
                    <td className="num text-xs">
                      {r.attachment_count > 0
                        ? <span className="inline-flex items-center gap-1 text-slate-600">
                            <Paperclip className="h-3 w-3" />{r.attachment_count}
                          </span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {newOpen && (
        <NewCreditNoteForm
          vendors={vendors}
          onClose={() => setNewOpen(false)}
          onDone={() => { setNewOpen(false); refresh() }}
        />
      )}

      {open && (
        <Modal open wide onClose={() => setOpen(null)}
               title={`Credit note ${open.credit_no}`}
               subtitle={`${open.id} · ${open.vendor_name}`}
               footer={<>
                 {mayDeleteCredit && (
                   <button className="btn-danger mr-auto"
                           onClick={() => setConfirmDelete(true)}>
                     Delete this credit note
                   </button>
                 )}
                 <button className="btn-secondary" onClick={() => setOpen(null)}>Close</button>
               </>}>
          <dl className="grid gap-4 sm:grid-cols-3">
            <D label="Vendor's credit number" value={open.credit_no} />
            <D label="Credit date" value={ddmmyyyy(open.credit_date)} />
            <D label="Status" value={open.credit_status === 'ACTIVE' ? 'Active' : 'Closed'} />
            <D label="Vendor" value={open.vendor_name} />
            <D label="Taxable value" value={rupees(open.taxable_amount)} />
            <D label="GST" value={rupees(open.gst_amount)} />
            <D label="Total credit" value={rupees(open.total_amount)} />
            <D label="Recorded by" value={open.recorded_by_name ?? people.name({ email: open.recorded_by_email })} />
            <D label="Recorded at" value={dateTime(open.created_at)} />
          </dl>

          <div className="mt-4 rounded-lg border border-slate-200 p-3">
            <p className="text-xs font-semibold text-slate-500">Reason given</p>
            <p className="mt-1 text-sm text-slate-800">{open.reason || '—'}</p>
          </div>

          <div className="mt-4">
            <Link to={`/invoices/${open.invoice_id}`} className="btn-secondary btn-sm">
              <FileText className="h-4 w-4" /> Open invoice {open.invoice_no} ({rupees(open.invoice_total)})
            </Link>
          </div>

          <div className="mt-4">
            <AttachmentList docType="CREDIT_NOTE" docId={open.id}
                            subtitle="The credit note document as the vendor issued it" />
          </div>

          {confirmDelete && (
            <DeleteCreditNote
              note={open}
              onClose={() => setConfirmDelete(false)}
              onDone={() => { setConfirmDelete(false); setOpen(null); refresh() }}
            />
          )}
        </Modal>
      )}
    </div>
  )
}

/* ----------------------------------------------------------------- payments */

export function Payments() {
  const { profile } = useAuth()
  // Names, not email addresses — management 01-09-2026. See src/lib/people.ts.
  const people = usePeople()
  const [vendor, setVendor] = useState('')
  const [mode, setMode] = useState('')
  const [q, setQ] = useState('')
  const [open, setOpen] = useState<any | null>(null)
  const [newOpen, setNewOpen] = useState(false)
  const [settleOpen, setSettleOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const toast = useToast()
  const { data: vendors } = useVendors()
  const { sort, toggle } = useSort('payment_date')

  async function cancelPayment(id: string, why: string) {
    try {
      const { data, error } = await supabase.rpc('rpc_cancel_payment',
        { p_payment_id: id, p_reason: why })
      if (error) throw new Error(error.message)
      toast.success(String(data ?? 'Cancelled.'))
      setCancelOpen(false); setOpen(null); refresh()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'It could not be cancelled')
    }
  }

  // Money leaving the bank is a Procurement decision. The Head of Stores can
  // see every payment but cannot make one.
  // Board 11-08-2026: Accounts records payments too — that is the whole of
  // its remit. Settling an unadjusted advance is NOT a payment; it is a
  // decision to write off company money, and it stays with the Head of
  // Procurement.
  const mayPay = can(profile!.role, 'recordPayment')
  const maySettle = can(profile!.role, 'settleAdvance')
  // 0142. Cancelling is narrower than recording and matches rpc_cancel_payment,
  // which is the authority: un-recording a payment is the only action here that
  // makes money that has already been reported disappear from a report. It
  // reuses the settleAdvance permission because that list is already exactly
  // "Head of Procurement and admin" and a second identical list would be a
  // second thing to keep in step.
  const mayCancel = maySettle
  // A Project Manager reads this screen for his own sites (11-09-2026). The
  // database decides which rows he gets; the screen only adds a Sites column
  // for him and hides the links into screens he cannot open. Every other role
  // sees exactly what it saw before.
  const isPm = profile!.role === 'project_manager'

  // CONTRACTOR PAYMENTS ARE NOT SHOWN HERE — 0141.
  //
  // Reported 17-09-2026: this screen listed four payments and two of them were
  // CPMT- payments against RA-1 of WO-TR7. The four tiles above are computed
  // from this list, so "Total paid" was a materials figure with 34,800 of
  // works-contract money inside it.
  //
  // Same fault, same fix and same reasoning as the CPAY payables in 0129: a
  // contractor payment has no invoice and no goods receipt, it settles a CPAY
  // payable against a running account bill, and its proof is the bank advice
  // filed on that bill. It belongs on Service > Payments, and the note under
  // the tiles says so rather than leaving a reader to wonder where it went.
  //
  // ra_bill_id is null is the test, not the CPMT prefix — a prefix is a label,
  // this is a fact about what the row points at.
  const { data: rows, loading, refresh } = useQuery<any>('v_payment_register', () => {
    let s = supabase.from('v_payment_register').select('*').is('ra_bill_id', null)
    if (vendor) s = s.eq('vendor_id', vendor)
    if (mode) s = s.eq('mode', mode)
    return s.order('payment_date', { ascending: false }).limit(1000)
  }, [vendor, mode], { cacheable: false })

  const modes = useMemo(
    () => [...new Set(rows.map((r: any) => r.mode).filter(Boolean))].sort() as string[], [rows])

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase()
    if (!t) return rows
    return rows.filter((r: any) => [r.id, r.vendor_name, r.invoice_no, r.reference_no, r.remarks,
                                    // 0130: a contractor payment has no invoice to
                                    // search by. Its bill and work order are what
                                    // somebody would actually type.
                                    r.ra_bill_id, r.wo_id,
                                    ...(isPm ? [r.site_names] : [])]
      .some((v) => String(v ?? '').toLowerCase().includes(t)))
  }, [rows, q, isPm])

  const sorted = useMemo(() => sortRows(filtered, sort, {
    payment_date: 'date', created_at: 'date', amount: 'number',
    invoice_total: 'number', attachment_count: 'number',
  }), [filtered, sort])

  const totals = useMemo(() => filtered.reduce((a: any, r: any) => ({
    paid: a.paid + Number(r.amount || 0),
    noproof: a.noproof + (r.attachment_count > 0 ? 0 : 1),
    unadjusted: a.unadjusted + Number(r.unadjusted_amount || 0),
  }), { paid: 0, noproof: 0, unadjusted: 0 }), [filtered])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-bold text-slate-800">Payments made</h1>
        <span className="pill-grey">{filtered.length}</span>
        {mayPay && (
          <button className="btn-primary btn-sm ml-auto" onClick={() => setNewOpen(true)}>
            <Plus className="h-4 w-4" /> New payment
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Payments" value={filtered.length} icon={<Wallet className="h-5 w-5" />} />
        <Stat label="Total paid" value={rupees(totals.paid)} tone="good" />
        <Stat label="Advance not yet adjusted" value={rupees(totals.unadjusted)}
              tone={totals.unadjusted > 0 ? 'warn' : 'good'}
              sub="Paid before delivery" icon={<HandCoins className="h-5 w-5" />} />
        <Stat label="Vendors paid"
              value={new Set(filtered.map((r: any) => r.vendor_id)).size} />
      </div>

      {totals.noproof > 0 && (
        <Alert tone="warning" title={`${totals.noproof} payment${totals.noproof === 1 ? '' : 's'} with no proof filed`}>
          The money left the bank but no screenshot or advice was attached. Sort by
          <strong> Proof</strong> to bring them to the top — an auditor will ask for these.
        </Alert>
      )}

      {/* Say where the contractor payments went, rather than silently omitting
          them. Exactly what 0129 did for the CPAY payables, and for the same
          reason: a man who saw two payments here yesterday and four the day
          before needs to be told, not left to conclude he has lost access. */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
        <Receipt className="mr-1 inline h-3.5 w-3.5" />
        These are payments against <strong>purchase invoices and vendor advances</strong>.
        Money paid to works contractors is numbered <strong>CPMT-</strong>, settles a
        CPAY- payable against a running account bill, and is on{' '}
        <strong>Service Procurement → Payments</strong>. They are the same ledger; only the
        doors differ.
      </div>

      <Card title={isPm ? 'Payments for your sites' : 'All payments'}
            subtitle="Click any row to see the full record and the proof filed with it">
        <div className="mb-3 grid gap-3 sm:grid-cols-3">
          <Field label="Vendor">
            <Select value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="All vendors"
                    options={vendors.map((v) => ({ value: v.id, label: v.company_name }))} />
          </Field>
          <Field label="Mode">
            <Select value={mode} onChange={(e) => setMode(e.target.value)} placeholder="All modes"
                    options={modes.map((m) => ({ value: m, label: m }))} />
          </Field>
          <Field label="Search">
            <SearchInput value={q} onChange={setQ} placeholder="Vendor, invoice, UTR / reference…" />
          </Field>
        </div>

        {loading && <Loading />}
        {!loading && sorted.length === 0 && (
          <EmptyState icon={<CreditCard className="h-6 w-6" />} title="No payments recorded"
                      message="Nothing has been paid to a vendor through the system yet." />
        )}
        {!loading && sorted.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Reference</Th>
                  <Th id="payment_date" sort={sort} onSort={toggle}>Paid on</Th>
                  <Th id="payment_type" sort={sort} onSort={toggle}>Type</Th>
                  <Th id="vendor_name" sort={sort} onSort={toggle}>Vendor</Th>
                  {isPm && <Th id="site_names" sort={sort} onSort={toggle}>Sites</Th>}
                  <Th id="invoice_no" sort={sort} onSort={toggle}>Against</Th>
                  <Th id="mode" sort={sort} onSort={toggle}>Mode</Th>
                  <Th id="reference_no" sort={sort} onSort={toggle}>UTR / reference</Th>
                  <Th id="amount" sort={sort} onSort={toggle} className="num" align="right">Amount</Th>
                  <Th id="adjustment_status" sort={sort} onSort={toggle}>Adjusted</Th>
                  <Th id="proof_status" sort={sort} onSort={toggle}>Proof</Th>
                  <Th id="recorded_by_name" sort={sort} onSort={toggle}>Recorded by</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r: any) => (
                  <tr key={r.id} className="cursor-pointer hover:bg-oval-50" onClick={() => setOpen(r)}>
                    <td className="font-mono text-xs">{r.id}</td>
                    <td className="text-xs">{ddmmyyyy(r.payment_date)}</td>
                    <td>
                      {r.payment_type === 'ADVANCE'
                        ? <span className="pill-violet">Advance</span>
                        : r.payment_type === 'PETTY_CASH'
                          ? <span className="pill-amber">Petty cash</span>
                          // 0130. Before this it fell through to "Invoice",
                          // which was simply untrue: a contractor payment has
                          // no invoice. The cash-out book shows every rupee
                          // that left the bank, so it must name what each one
                          // was for.
                          : r.payment_type === 'AGAINST_RA_BILL'
                            ? <span className="pill-blue">Contractor bill</span>
                            : <span className="pill-grey">Invoice</span>}
                    </td>
                    <td className="text-sm font-medium">{r.vendor_name}</td>
                    {isPm && (
                      <td className="max-w-[200px] truncate text-xs" title={r.site_names ?? ''}>
                        {r.site_names ?? '—'}
                      </td>
                    )}
                    <td className="font-mono text-xs">
                      {/* 0130. A contractor payment is against a running
                          account bill, not an invoice or an order. Plain text,
                          not a link: this is the Materials module and the
                          reader may have no Service access at all — an
                          inviting link that lands on "Not open to your role"
                          is worse than the number on its own. */}
                      {r.ra_bill_id
                        ? <>
                            RA-{r.ra_no}
                            <span className="block text-[11px] text-slate-400">{r.wo_id}</span>
                          </>
                        : r.invoice_no ?? r.po_id ?? '—'}
                    </td>
                    <td className="text-xs">{r.mode}</td>
                    <td className="font-mono text-xs text-slate-500">{r.reference_no ?? '—'}</td>
                    <td className="num text-sm font-bold text-slate-900">{rupees(r.amount)}</td>
                    <td className="text-xs">
                      {r.payment_type !== 'ADVANCE' ? <span className="text-slate-300">—</span>
                        : r.adjustment_status === 'Fully adjusted'
                          ? <span className="pill-green">Fully adjusted</span>
                          : <span className="pill-amber" title={`${rupees(r.unadjusted_amount)} still unadjusted`}>
                              {r.adjustment_status}
                            </span>}
                    </td>
                    <td>
                      {/*
                        A petty-cash purchase carries its proof on the goods
                        receipt — the shop's bill, which the store-man had to
                        attach before he could even submit it. Saying which one
                        it is matters: a bank advice and a cash memo are
                        different evidence, and an auditor will want to know
                        which he is being shown.
                      */}
                      {r.attachment_count > 0
                        ? (r.proof_source === 'GRN'
                            ? <span className="pill-green" title={`Attached to goods receipt ${r.grn_id}`}>
                                On the receipt
                              </span>
                            : <span className="pill-green">Filed</span>)
                        : <span className="pill-amber">Missing</span>}
                    </td>
                    <td className="max-w-[160px] truncate text-xs text-slate-500">
                      {r.recorded_by_name ?? people.name({ email: r.recorded_by_email })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {newOpen && (
        <NewPaymentForm
          vendors={vendors}
          onClose={() => setNewOpen(false)}
          onDone={() => { setNewOpen(false); refresh() }}
        />
      )}

      {open && (
        <Modal open wide onClose={() => setOpen(null)}
               title={`${open.payment_type === 'ADVANCE' ? 'Advance'
                        : open.payment_type === 'AGAINST_RA_BILL' ? 'Contractor payment'
                        : 'Payment'} ${rupees(open.amount)}`}
               subtitle={`${open.id} · ${open.vendor_name}`}
               footer={<button className="btn-secondary" onClick={() => setOpen(null)}>Close</button>}>
          <dl className="grid gap-4 sm:grid-cols-3">
            <D label="Amount" value={rupees(open.amount)} />
            <D label="Paid on" value={ddmmyyyy(open.payment_date)} />
            <D label="Mode" value={open.mode ?? '—'} />
            <D label="Vendor" value={open.vendor_name} />
            <D label="UTR / reference" value={open.reference_no ?? '—'} />
            {open.ra_bill_id
              // 0130. Naming the bill and the order is the whole answer to
              // "what was this money for" for a works contractor. A purchase
              // order line here would always read "—".
              ? <>
                  <D label="Against running account bill"
                     value={`${open.ra_bill_id} · RA-${open.ra_no}`} />
                  <D label="Work order" value={open.wo_id ?? '—'} />
                </>
              : <D label="Against purchase order" value={open.po_id ?? '—'} />}
            {isPm && <D label="Sites" value={open.site_names ?? '—'} />}
            {isPm && open.invoice_id && <D label="Against invoice" value={open.invoice_no ?? open.invoice_id} />}
            <D label="Recorded by" value={open.recorded_by_name ?? people.name({ email: open.recorded_by_email })} />
            <D label="Recorded at" value={dateTime(open.created_at)} />
            <D label="Proof on file"
               value={open.attachment_count === 0 ? 'No'
                    : open.proof_source === 'GRN' ? `Yes — on goods receipt ${open.grn_id}`
                    : open.proof_source === 'RA' ? `Yes — the advice is on bill ${open.ra_bill_id}`
                    : 'Yes'} />
            {open.grn_id && <D label="Settles goods receipt" value={open.grn_id} />}
            {open.payment_type === 'ADVANCE' && <>
              <D label="Adjusted so far" value={rupees(open.adjusted_amount)} />
              <D label="Still unadjusted" value={rupees(open.unadjusted_amount)} />
            </>}
          </dl>

          {open.payment_type === 'ADVANCE' && !isPm && (
            <div className="mt-4">
              <AdvanceBreakdown paymentId={open.id} poId={open.po_id} />
            </div>
          )}

          {/*
            An advance can only be adjusted while more deliveries are coming.
            Once the order is finished, whatever is left is real money the
            vendor is holding — it has to be recovered or consciously written
            off, not left to sit on a screen for ever.
          */}
          {open.payment_type === 'ADVANCE' && Number(open.unadjusted_amount) > 0.005 && maySettle && (
            <div className="mt-4">
              <Alert tone="warning"
                     title={`${rupees(open.unadjusted_amount)} of this advance can no longer be adjusted`}>
                It will only reduce further if another delivery arrives on {open.po_id}. If that
                order is finished, this is money the vendor is holding for goods he never sent —
                recover it, or record that it has been written off.
                <div className="mt-3">
                  <button className="btn-secondary btn-sm" onClick={() => setSettleOpen(true)}>
                    Settle this balance
                  </button>
                </div>
              </Alert>
            </div>
          )}

          {settleOpen && (
            <SettleAdvance
              payment={open}
              onClose={() => setSettleOpen(false)}
              onDone={() => { setSettleOpen(false); setOpen(null); refresh() }}
            />
          )}

          {/*
            A PAYMENT THAT WAS NEVER MADE — 0142.
            Deliberately NOT beside "Settle this balance", and deliberately
            worded as the opposite of it. Settling says the money left the bank
            and was refunded or written off; this says it never left at all.
            Confusing the two puts a fictional loss in the books, so the two
            controls are kept apart and each says what the other is not.
          */}
          {mayCancel && (
            <div className="mt-4">
              <button className="btn-ghost btn-sm text-red-600"
                      onClick={() => setCancelOpen(true)}>
                <Ban className="h-4 w-4" /> This payment was never made
              </button>
              <p className="mt-1 text-xs text-slate-500">
                For a duplicate or a mis-keyed entry only. If the money did leave the bank
                and came back, that is a refund — settle it instead.
              </p>
            </div>
          )}

          <Confirm open={cancelOpen}
                   title={`Cancel ${open.id}?`}
                   tone="danger" confirmLabel="Cancel this payment"
                   requireReason
                   reasonLabel="What actually happened (this is the only record that survives)"
                   message={<>
                     {rupees(open.amount)} comes off the ledger and off every report that
                     counts it. Use this only when the money <strong>never left the bank</strong> —
                     a duplicate, or an entry against the wrong document. If it was paid and
                     refunded, close this and settle it as a refund instead.
                   </>}
                   onConfirm={(why?: string) => void cancelPayment(open.id, why ?? '')}
                   onCancel={() => setCancelOpen(false)} />

          {open.remarks && (
            <div className="mt-4 rounded-lg border border-slate-200 p-3">
              <p className="text-xs font-semibold text-slate-500">Remarks</p>
              <p className="mt-1 text-sm text-slate-800">{open.remarks}</p>
            </div>
          )}

          {open.invoice_id && !isPm && (
            <div className="mt-4">
              <Link to={`/invoices/${open.invoice_id}`} className="btn-secondary btn-sm">
                <FileText className="h-4 w-4" /> Open invoice {open.invoice_no} ({rupees(open.invoice_total)})
              </Link>
            </div>
          )}

          {open.attachment_count === 0 && !isPm && (
            <div className="mt-4">
              <Alert tone="warning" title="No proof was filed with this payment">
                Open the invoice and attach the bank screenshot or payment advice. Without it there
                is nothing to show an auditor that this money actually moved.
              </Alert>
            </div>
          )}

          <div className="mt-4">
            {open.proof_source === 'GRN' ? (
              // The bill lives on the receipt, so show the receipt's files —
              // not an empty "no proof" box beside a payment that is fully
              // evidenced one document away.
              <AttachmentList
                docType="GRN" docId={open.grn_id}
                subtitle={`The shop's bill, attached to goods receipt ${open.grn_id} when the material arrived`} />
            ) : (
              <AttachmentList docType="PAYMENT" docId={open.id}
                              subtitle="Bank screenshot, payment advice, cheque image" />
            )}
          </div>
        </Modal>
      )}
    </div>
  )
}

const D = ({ label, value }: { label: string; value: string }) => (
  <div>
    <dt className="text-xs font-semibold text-slate-500">{label}</dt>
    <dd className="mt-0.5 break-words text-sm text-slate-800">{value}</dd>
  </div>
)

/* --------------------------------------------------- recording a credit note */

/**
 * A credit note recorded from the register, rather than from inside the
 * invoice it belongs to.
 *
 * The reason this screen needs its own button: the credit usually arrives
 * WEEKS after the invoice was matched and paid — the rejected material goes
 * back to the vendor, he inspects it, he argues, and eventually he issues the
 * note. By then nobody remembers which invoice it was, and hunting for it one
 * payable at a time is how people end up short-paying the next bill instead,
 * which is exactly what a credit note exists to prevent.
 *
 * It still attaches to an invoice, because a credit with nothing to credit is
 * not a document — it is a wish. Choosing the vendor first narrows the list to
 * his own bills and shows how much of each is still creditable.
 */
function NewCreditNoteForm({ vendors, onClose, onDone }: {
  vendors: any[]
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [vendorId, setVendorId] = useState('')
  const [invoiceId, setInvoiceId] = useState('')
  const [creditNo, setCreditNo] = useState('')
  const [creditDate, setCreditDate] = useState(todayIso)
  const [taxable, setTaxable] = useState('')
  const [gst, setGst] = useState('')
  const [reason, setReason] = useState('')
  const [files, setFiles] = useState<PendingAttachment[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [invoices, setInvoices] = useState<any[]>([])

  useEffect(() => {
    if (!vendorId) { setInvoices([]); return }
    let alive = true
    void (async () => {
      // ra_bill_id is null excludes contractor invoices: a credit note against
      // a works contract is raised on the Service side, where the RA bill and
      // its retention live. Migration 0140.
      const { data } = await supabase.from('vendor_invoices')
        .select('id, invoice_no, invoice_date, total_amount, credited_amount, status')
        .eq('vendor_id', vendorId).neq('status', 'CANCELLED').is('ra_bill_id', null)
        .order('invoice_date', { ascending: false }).limit(200)
      if (alive) setInvoices(data ?? [])
    })()
    return () => { alive = false }
  }, [vendorId])

  const chosen = invoices.find((i) => i.id === invoiceId)
  const creditable = chosen
    ? Number(chosen.total_amount || 0) - Number(chosen.credited_amount || 0)
    : 0
  const total = Math.round((Number(taxable || 0) + Number(gst || 0)) * 100) / 100

  async function save() {
    setErr(null)
    if (!vendorId) { setErr('Choose the vendor.'); return }
    if (!invoiceId) { setErr('Choose the invoice this credit note is against.'); return }
    if (!creditNo.trim()) { setErr("Enter the vendor's own credit note number."); return }
    if (!(total > 0)) { setErr('Enter the taxable value, and the GST if there is any.'); return }
    if (total > creditable + 0.01) {
      setErr(`That credits ${rupees(total)} against an invoice with only ${rupees(creditable)} left to credit.`)
      return
    }
    if (reason.trim().length < 5) { setErr('Say why the vendor issued this credit note.'); return }

    setBusy(true)
    try {
      const { data, error } = await supabase.rpc('rpc_record_credit_note', {
        p_invoice_id: invoiceId,
        p_credit_no: creditNo.trim(),
        p_credit_date: creditDate,
        p_taxable_amount: Number(taxable || 0),
        p_gst_amount: Number(gst || 0),
        p_reason: reason.trim(),
      })
      if (error) throw new Error(error.message)
      if (files.length) await queueAttachments('CREDIT_NOTE', data as string, files)
      toast.success('Credit note recorded. What the vendor is owed has come down by that amount.')
      onDone()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not record the credit note')
    } finally { setBusy(false) }
  }

  return (
    <Modal open wide onClose={onClose}
           title="Record a credit note"
           subtitle="The vendor admitting he billed for more than you kept"
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy}>
               {busy ? 'Recording…' : 'Record credit note'}
             </button>
           </>}>
      <div className="space-y-4">
        {err && <Alert tone="error">{err}</Alert>}

        <Alert tone="info" title="Never short-pay instead of this">
          If a vendor billed for material you rejected, ask him for a credit note and record it
          here. It reduces what he is owed and leaves both sides holding the same paperwork.
          Quietly paying him less is how reconciliation disputes start — and you will lose the
          argument two years later when nobody remembers why.
        </Alert>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Vendor" required>
            <Select value={vendorId}
                    onChange={(e) => { setVendorId(e.target.value); setInvoiceId('') }}
                    placeholder="Choose the vendor…"
                    options={vendors.map((v) => ({ value: v.id, label: v.company_name }))} />
          </Field>

          <Field label="Against invoice" required
                 hint={chosen
                   ? `Billed ${rupees(chosen.total_amount)} · already credited ${rupees(chosen.credited_amount)} · at most ${rupees(creditable)} more`
                   : 'His own bills only'}>
            <Select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}
                    placeholder={vendorId ? 'Choose the invoice…' : 'Choose a vendor first'}
                    options={invoices.map((i) => ({
                      value: i.id,
                      label: `${i.invoice_no} · ${rupees(i.total_amount)} · ${ddmmyyyy(i.invoice_date)}`,
                    }))} />
          </Field>

          <Field label="Vendor's credit note number" required>
            <Input value={creditNo} onChange={(e) => setCreditNo(e.target.value)}
                   placeholder="As printed on his note, e.g. CN/2026/017" />
          </Field>

          <DocumentDate label="Credit note date" value={creditDate} onChange={setCreditDate}
                        hint="The date on the vendor's note, not the day you keyed it in." />

          <Field label="Taxable value (₹)" required>
            <Input type="number" inputMode="decimal" step="any" min="0"
                   value={taxable} onChange={(e) => setTaxable(e.target.value)} />
          </Field>

          <Field label="GST credited (₹)"
                 hint="He must reverse the tax too, at the rate he charged.">
            <Input type="number" inputMode="decimal" step="any" min="0"
                   value={gst} onChange={(e) => setGst(e.target.value)} />
          </Field>

          <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">Total credit</span>
              <span className="text-base font-bold text-emerald-700">{rupees(total)}</span>
            </div>
            {chosen && total > creditable + 0.01 && (
              <p className="mt-2 text-xs font-semibold text-rose-600">
                That is more than the {rupees(creditable)} still creditable on this invoice.
              </p>
            )}
          </div>

          <Field label="Why he issued it" required className="sm:col-span-2"
                 hint="Kept permanently. An auditor will read this.">
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g. 5 rolls rejected at TR5 on 14-08-2026 for damaged insulation and returned on his vehicle. Credit agreed by telephone with Mr Saha." />
          </Field>
        </div>

        <AttachmentCollector docType="CREDIT_NOTE" value={files} onChange={setFiles}
                             hint="The credit note as the vendor issued it, and the rejection note if there is one." />
      </div>
    </Modal>
  )
}

/**
 * Removing a credit note.
 *
 * Kept deliberately blunt. A credit note is a real document from a real
 * vendor, and deleting one silently raises what he is owed — so the only
 * honest reasons are a keying mistake or a test entry. Anything else is a
 * matter for a second credit note, not an eraser.
 */
function DeleteCreditNote({ note, onClose, onDone }: {
  note: any
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function go() {
    setErr(null); setBusy(true)
    try {
      const { error } = await supabase.rpc('rpc_delete_credit_note', { p_credit_note_id: note.id })
      if (error) throw new Error(error.message)
      toast.success('Credit note deleted. What the vendor is owed has gone back up by that amount.')
      onDone()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not delete the credit note')
    } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose}
           title={`Delete credit note ${note.credit_no}?`}
           subtitle={`${rupees(note.total_amount)} · ${note.vendor_name}`}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Keep it</button>
             <button className="btn-danger" onClick={go} disabled={busy}>
               {busy ? 'Deleting…' : 'Delete it'}
             </button>
           </>}>
      <Alert tone="warning" title="What this does">
        {rupees(note.total_amount)} goes straight back onto what {note.vendor_name} is owed, and
        the payables behind invoice {note.invoice_no} move with it.
        <p className="mt-2">
          Only do this if the note was keyed by mistake or was a test. If the vendor genuinely
          issued it and then withdrew it, ask him for a document saying so and file that instead —
          deleting his paperwork leaves you with nothing to show for the argument.
        </p>
      </Alert>
      {err && <div className="mt-3"><Alert tone="error">{err}</Alert></div>}
    </Modal>
  )
}

/* ------------------------------------------------------- recording a payment */

/**
 * Money going out to a vendor, in the two shapes it actually takes.
 *
 * ADVANCE — paid against a purchase order before anything is delivered,
 * because the order says so ("30% Advance + Balance Before Dispatch"). There
 * is no invoice yet and no payable; the money is simply owed back to us in
 * goods. As each delivery raises a payable, the advance is applied to it
 * automatically, in the same proportion the advance bore to the order.
 *
 * AGAINST INVOICE — the ordinary settlement of a bill already received.
 *
 * Both are the same table underneath, which is what lets the payables screen
 * show one honest "still to pay" figure.
 */
function NewPaymentForm({ vendors, onClose, onDone }: {
  vendors: any[]
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [kind, setKind] = useState<'ADVANCE' | 'AGAINST_INVOICE'>('ADVANCE')
  const [vendorId, setVendorId] = useState('')
  const [poId, setPoId] = useState('')
  const [invoiceId, setInvoiceId] = useState('')
  const [amount, setAmount] = useState('')
  const [payDate, setPayDate] = useState(todayIso)
  const [mode, setMode] = useState('NEFT')
  const [ref, setRef] = useState('')
  const [remarks, setRemarks] = useState('')
  const [files, setFiles] = useState<PendingAttachment[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const [pos, setPos] = useState<any[]>([])
  const [invoices, setInvoices] = useState<any[]>([])

  // Orders that can still take an advance: approved and not yet dead.
  useEffect(() => {
    if (!vendorId || kind !== 'ADVANCE') { setPos([]); return }
    let alive = true
    void (async () => {
      const { data } = await supabase.from('purchase_orders')
        .select('id, total_amount, status, date_created, payment_terms')
        .eq('vendor_id', vendorId)
        .in('status', ['APPROVED', 'VENDIN', 'VENDACK', 'PARTIALLY_RECEIVED', 'CLOSED', 'FULFILLED'])
        .order('date_created', { ascending: false }).limit(200)
      if (alive) setPos(data ?? [])
    })()
    return () => { alive = false }
  }, [vendorId, kind])

  useEffect(() => {
    if (!vendorId || kind !== 'AGAINST_INVOICE') { setInvoices([]); return }
    let alive = true
    void (async () => {
      // ra_bill_id is null again: a contractor is paid AGAINST_RA_BILL through
      // rpc_ra_record_payment, never AGAINST_INVOICE. Offering his invoice here
      // would record a payment the RA bill knows nothing about and the
      // contractor ledger would double-count it. Migration 0140.
      const { data } = await supabase.from('vendor_invoices')
        .select('id, invoice_no, invoice_date, total_amount, status')
        .eq('vendor_id', vendorId).neq('status', 'CANCELLED').is('ra_bill_id', null)
        .order('invoice_date', { ascending: false }).limit(200)
      if (alive) setInvoices(data ?? [])
    })()
    return () => { alive = false }
  }, [vendorId, kind])

  const chosenPo = pos.find((p) => p.id === poId)

  async function save() {
    setErr(null)
    if (!vendorId) { setErr('Choose the vendor.'); return }
    if (kind === 'ADVANCE' && !poId) { setErr('Choose the purchase order the advance is against.'); return }
    if (kind === 'AGAINST_INVOICE' && !invoiceId) { setErr('Choose the invoice being paid.'); return }
    if (!(Number(amount) > 0)) { setErr('Enter the amount paid.'); return }

    setBusy(true)
    try {
      const { data, error } = kind === 'ADVANCE'
        ? await supabase.rpc('rpc_record_advance', {
            p_po_id: poId, p_amount: Number(amount), p_payment_date: payDate,
            p_mode: mode, p_reference_no: ref || null, p_remarks: remarks || null,
          })
        : await supabase.rpc('rpc_record_payment', {
            p_invoice_id: invoiceId, p_amount: Number(amount), p_payment_date: payDate,
            p_mode: mode, p_reference_no: ref || null, p_remarks: remarks || null,
          })
      if (error) throw new Error(error.message)
      if (files.length) await queueAttachments('PAYMENT', data as string, files)
      toast.success(kind === 'ADVANCE'
        ? 'Advance recorded. It will be set against each delivery as the goods arrive.'
        : 'Payment recorded.')
      onDone()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not record the payment')
    } finally { setBusy(false) }
  }

  return (
    <Modal open wide onClose={onClose}
           title="Record a payment"
           subtitle="Advance against an order, or settlement of an invoice"
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy}>
               {busy ? 'Recording…' : 'Record payment'}
             </button>
           </>}>
      <div className="space-y-4">
        {err && <Alert tone="error">{err}</Alert>}

        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          {([
            ['ADVANCE', 'Advance against an order'],
            ['AGAINST_INVOICE', 'Against an invoice'],
          ] as const).map(([k, label]) => (
            <button key={k}
                    onClick={() => { setKind(k); setPoId(''); setInvoiceId('') }}
                    className={`flex-1 rounded-md px-3 py-1.5 text-xs font-semibold ${
                      kind === k ? 'bg-white text-oval-800 shadow-sm' : 'text-slate-600'}`}>
              {label}
            </button>
          ))}
        </div>

        {kind === 'ADVANCE' && (
          <Alert tone="info" title="What happens to an advance">
            The money is recorded against the order, not against a bill. As each delivery raises a
            payable, the system sets part of the advance against it — in the same proportion the
            advance bore to the order, so a 30% advance pre-pays 30% of every load. Anything left
            unadjusted when the order finishes is money the vendor still owes you in goods.
          </Alert>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Vendor" required>
            <Select value={vendorId}
                    onChange={(e) => { setVendorId(e.target.value); setPoId(''); setInvoiceId('') }}
                    placeholder="Choose the vendor…"
                    options={vendors.map((v) => ({ value: v.id, label: v.company_name }))} />
          </Field>

          {kind === 'ADVANCE' ? (
            <Field label="Against purchase order" required
                   hint={chosenPo
                     ? `Order value ${rupees(chosenPo.total_amount)} · ${chosenPo.payment_terms ?? 'no terms recorded'}`
                     : 'Only approved orders can take an advance'}>
              <Select value={poId} onChange={(e) => setPoId(e.target.value)}
                      placeholder={vendorId ? 'Choose the order…' : 'Choose a vendor first'}
                      options={pos.map((p) => ({
                        value: p.id,
                        label: `${p.id} · ${rupees(p.total_amount)} · ${ddmmyyyy(p.date_created)}`,
                      }))} />
            </Field>
          ) : (
            <Field label="Against invoice" required>
              <Select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}
                      placeholder={vendorId ? 'Choose the invoice…' : 'Choose a vendor first'}
                      options={invoices.map((i) => ({
                        value: i.id,
                        label: `${i.invoice_no} · ${rupees(i.total_amount)} · ${ddmmyyyy(i.invoice_date)}`,
                      }))} />
            </Field>
          )}

          <Field label="Amount paid (₹)" required
                 hint={kind === 'ADVANCE' && chosenPo
                   ? `30% of this order would be ${rupees(Number(chosenPo.total_amount) * 0.3)}`
                   : undefined}>
            <Input type="number" inputMode="decimal" step="any" min="0"
                   value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>

          <DocumentDate label="Paid on" value={payDate} onChange={setPayDate}
                        hint="The day the money actually left the bank." />

          <Field label="Mode" required>
            <Select value={mode} onChange={(e) => setMode(e.target.value)}
                    options={PAYMENT_MODES.map((m) => ({ value: m, label: m }))} />
          </Field>

          <Field label="UTR / reference">
            <Input value={ref} onChange={(e) => setRef(e.target.value)}
                   placeholder="Bank reference for this transfer" />
          </Field>

          <Field label="Remarks" className="sm:col-span-2">
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </Field>
        </div>

        <AttachmentCollector docType="PAYMENT" value={files} onChange={setFiles}
                             hint="The bank screenshot or payment advice. An auditor will ask for it." />
      </div>
    </Modal>
  )
}

/** Which deliveries an advance has been set against. */
function AdvanceBreakdown({ paymentId, poId }: { paymentId: string; poId: string | null }) {
  const [rows, setRows] = useState<any[]>([])

  useEffect(() => {
    let alive = true
    void (async () => {
      const { data } = await supabase.from('advance_adjustments')
        .select('payable_id, amount, adjusted_at')
        .eq('payment_id', paymentId).order('adjusted_at')
      if (alive) setRows(data ?? [])
    })()
    return () => { alive = false }
  }, [paymentId])

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <p className="text-xs font-semibold text-slate-500">
        Set against these deliveries
      </p>
      {rows.length === 0 ? (
        <p className="mt-1 text-sm text-slate-600">
          Nothing yet — no goods have been received against
          {poId ? ` ${poId}` : ' this order'}. The advance is applied automatically as each
          delivery is approved.
        </p>
      ) : (
        <table className="tbl mt-2">
          <thead><tr><th>Payable</th><th className="num">Applied</th><th>When</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.payable_id}>
                <td className="font-mono text-xs">
                  <Link to={`/payables/${r.payable_id}`} className="text-oval-700 hover:underline">
                    {r.payable_id}
                  </Link>
                </td>
                <td className="num text-sm font-semibold">{rupees(r.amount)}</td>
                <td className="text-xs text-slate-500">{dateTime(r.adjusted_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/** Recovering — or writing off — an advance that can no longer be adjusted. */
function SettleAdvance({ payment, onClose, onDone }: {
  payment: any
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [outcome, setOutcome] = useState<'REFUNDED' | 'WRITTEN_OFF'>('REFUNDED')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function save() {
    setErr(null)
    if (reason.trim().length < 10) { setErr('Write what happened to the money — a short sentence is enough.'); return }
    setBusy(true)
    try {
      const { error } = await supabase.rpc('rpc_settle_advance', {
        p_payment_id: payment.id, p_outcome: outcome, p_reason: reason.trim(),
      })
      if (error) throw new Error(error.message)
      toast.success(outcome === 'REFUNDED'
        ? 'Recorded as refunded. The advance no longer shows as outstanding.'
        : 'Written off. The decision and the reason are on the record.')
      onDone()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not settle the advance')
    } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose}
           title={`Settle ${rupees(payment.unadjusted_amount)} still on this advance`}
           subtitle={`${payment.id} · ${payment.vendor_name}`}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy}>
               {busy ? 'Saving…' : 'Record it'}
             </button>
           </>}>
      <div className="space-y-4">
        {err && <Alert tone="error">{err}</Alert>}

        <Field label="What happened to the money" required>
          <Select value={outcome} onChange={(e) => setOutcome(e.target.value as any)}
                  options={[
                    { value: 'REFUNDED', label: 'The vendor refunded it' },
                    { value: 'WRITTEN_OFF', label: 'Written off — not worth chasing' },
                  ]} />
        </Field>

        <Field label="Explain briefly" required
               hint="Kept permanently against the order. An auditor will read this.">
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder={outcome === 'REFUNDED'
                      ? 'e.g. Refunded by NEFT on 20-08-2026, UTR NEFT44821, against short delivery of 5 rolls and 3 pipes.'
                      : 'e.g. Vendor has ceased trading; amount below the cost of recovery. Approved by the Procurement Manager.'} />
        </Field>

        <Alert tone="info" title="This does not touch the deliveries">
          Everything already received stays received and stays payable. This only closes off the
          part of the advance that no delivery will ever absorb.
        </Alert>
      </div>
    </Modal>
  )
}
